/**
 * @description: Reusable Payment Status Badge Component
 * @author: USER
 * @created: 06/09/2025
 * @version: 2.0.0 - Enhanced with better error handling and fee parsing
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { adminAPI } from '../services/api';
import { FaTimes, FaSpinner } from 'react-icons/fa';

// Helper function to parse fees from various formats
const parseFee = (fee) => {
  if (!fee) return 0;
  if (typeof fee === 'number') return fee;
  if (typeof fee === 'string') {
    // Handle "Free" or empty strings
    if (fee.toLowerCase().includes('free') || fee.trim() === '') return 0;
    
    // Extract numbers from strings like "KES 2,000" or "2000"
    const match = fee.match(/[\d,]+/);
    if (match) {
      return parseFloat(match[0].replace(/,/g, ''));
    }
  }
  return 0;
};

// Payment Status Badge Component for Events with Balance Calculation
export const PaymentStatusBadge = ({ participant, eventId, eventFee, currency = 'KES', mobile = false, onUpdate }) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');

  // Calculate balance with improved parsing
  const registrationFee = parseFee(eventFee);
  const amountPaid = parseFloat(participant?.paymentAmount) || 0;
  const balance = Math.max(0, registrationFee - amountPaid);
  
  const handleUpdatePayment = async () => {
    const amount = parseFloat(paymentAmount);
    
    if (!paymentAmount || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (amount > balance) {
      toast.error(`Payment amount cannot exceed balance of ${currency} ${balance.toLocaleString()}`);
      return;
    }

    const newPaymentAmount = amountPaid + amount;
    const newBalance = Math.max(0, registrationFee - newPaymentAmount);

    setLoading(true);
    try {
      const response = await adminAPI.updateParticipantPayment(eventId, participant._id, {
        paymentAmount: newPaymentAmount,
        paymentStatus: newBalance === 0 ? 'cleared' : 'partial',
        paymentDate: new Date().toISOString()
      });

      if (response.success) {
        toast.success(
          newBalance === 0
            ? '✓ Payment cleared successfully!'
            : `Payment updated! Remaining balance: ${currency} ${newBalance.toLocaleString()}`
        );
        onUpdate(response.participant);
        setShowPaymentModal(false);
        setPaymentAmount('');
      } else {
        throw new Error(response.message || 'Failed to update payment');
      }
    } catch (error) {
      console.error('Payment update error:', error);
      toast.error(error.message || 'Failed to update payment status');
    } finally {
      setLoading(false);
    }
  };

  const isCleared = balance === 0 || participant?.paymentStatus === 'cleared';
  const isPartial = balance > 0 && amountPaid > 0;

  // Don't show payment badge if registration is free
  if (registrationFee === 0) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        Free Event
      </span>
    );
  }

  if (mobile) {
    return (
      <>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Payment Status:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              isCleared ? 'bg-green-100 text-green-800' : 
              isPartial ? 'bg-blue-100 text-blue-800' : 
              'bg-yellow-100 text-yellow-800'
            }`}>
              {isCleared ? '✓ Cleared' : isPartial ? '⚠ Partial' : '⏳ Pending'}
            </span>
          </div>
          
          <div className="text-xs space-y-1 bg-gray-50 rounded-lg p-2 border border-gray-200">
            <div className="flex justify-between">
              <span className="text-gray-600">Registration Fee:</span>
              <span className="font-medium">{currency} {registrationFee.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Amount Paid:</span>
              <span className="font-medium text-green-600">{currency} {amountPaid.toLocaleString()}</span>
            </div>
            {balance > 0 && (
              <div className="flex justify-between border-t pt-1 mt-1">
                <span className="text-gray-700 font-semibold">Balance Due:</span>
                <span className="font-bold text-red-600">{currency} {balance.toLocaleString()}</span>
              </div>
            )}
          </div>
          
          {!isCleared && (
            <button
              onClick={() => setShowPaymentModal(true)}
              className="w-full px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600 transition-colors font-medium"
            >
              💳 Update Payment
            </button>
          )}
        </div>

        {/* Mobile Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
            <div className="bg-white rounded-2xl p-4 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold text-gray-900">Update Payment</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">
                <span className="font-semibold">{participant?.userId?.name || participant?.userId?.teamName || participant?.userId?.schoolName || 'Participant'}</span>
              </p>
              
              <div className="mb-4 space-y-2">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-3 text-sm border border-blue-200">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-700">Registration Fee:</span>
                    <span className="font-semibold">{currency} {registrationFee.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-700">Already Paid:</span>
                    <span className="font-semibold text-green-600">{currency} {amountPaid.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-blue-300">
                    <span className="text-gray-900 font-bold">Balance Due:</span>
                    <span className="font-bold text-red-600">{currency} {balance.toLocaleString()}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Amount ({currency}) *
                  </label>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Max: ${balance.toLocaleString()}`}
                    max={balance}
                    min="1"
                    step="0.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter the amount received from participant</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleUpdatePayment}
                  disabled={loading || !paymentAmount}
                  className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    '✓ Update Payment'
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmount('');
                  }}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop version
  return (
    <>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            isCleared ? 'bg-green-100 text-green-800' : 
            isPartial ? 'bg-blue-100 text-blue-800' : 
            'bg-yellow-100 text-yellow-800'
          }`}>
            {isCleared ? '✓ Cleared' : 
             isPartial ? `⚠ Partial (${Math.round((amountPaid/registrationFee)*100)}%)` : 
             '⏳ Pending'}
          </span>
          {!isCleared && (
            <button
              onClick={() => setShowPaymentModal(true)}
              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors font-medium"
            >
              Update
            </button>
          )}
        </div>
        
        <div className="text-xs text-gray-600">
          <div>Paid: <span className="font-medium text-green-600">{currency} {amountPaid.toLocaleString()}</span> / {registrationFee.toLocaleString()}</div>
          {balance > 0 && (
            <div className="text-red-600 font-semibold">Balance: {currency} {balance.toLocaleString()}</div>
          )}
        </div>
      </div>

      {/* Desktop Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Update Payment Status</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600">
                Participant: <span className="font-semibold">{participant?.userId?.name || participant?.userId?.teamName || participant?.userId?.schoolName || 'Unknown'}</span>
              </p>
              {participant?.userId?.email && (
                <p className="text-sm text-gray-500">{participant.userId.email}</p>
              )}
            </div>
            
            <div className="mb-4 space-y-3">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-700">Registration Fee:</span>
                  <span className="font-semibold">{currency} {registrationFee.toLocaleString()}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-700">Already Paid:</span>
                  <span className="font-semibold text-green-600">{currency} {amountPaid.toLocaleString()}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-blue-300">
                  <span className="text-gray-900 font-bold">Balance Due:</span>
                  <span className="font-bold text-red-600">{currency} {balance.toLocaleString()}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Amount ({currency}) *
                </label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Enter amount (Max: ${balance.toLocaleString()})`}
                  max={balance}
                  min="1"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">Enter the amount received from the participant</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleUpdatePayment}
                disabled={loading || !paymentAmount}
                className="flex-1 bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Updating...
                  </>
                ) : (
                  '✓ Update Payment'
                )}
              </button>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentAmount('');
                }}
                disabled={loading}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Program Payment Status Badge Component
export const ProgramPaymentBadge = ({ enrollment, programId, mobile = false, onUpdate }) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  
  const handleUpdatePayment = async () => {
    const amount = parseFloat(paymentAmount);
    
    if (!paymentAmount || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setLoading(true);
    try {
      const response = await adminAPI.updateProgramEnrollmentPayment(programId, enrollment._id, {
        paymentAmount: amount,
        paymentStatus: 'cleared',
        paymentDate: new Date().toISOString()
      });

      if (response.success) {
        toast.success('✓ Payment cleared successfully!');
        onUpdate(response.enrollment);
        setShowPaymentModal(false);
        setPaymentAmount('');
      } else {
        throw new Error(response.message || 'Failed to update payment');
      }
    } catch (error) {
      console.error('Payment update error:', error);
      toast.error(error.message || 'Failed to update payment status');
    } finally {
      setLoading(false);
    }
  };

  const isCleared = enrollment?.paymentStatus === 'cleared' || enrollment?.paymentStatus === 'paid';
  const paymentAmountDisplay = enrollment?.paymentAmount ? `KES ${enrollment.paymentAmount.toLocaleString()}` : '';

  if (mobile) {
    return (
      <>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Payment:</span>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              isCleared ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {isCleared ? '✓ Cleared' : '⏳ Pending'}
              {paymentAmountDisplay && ` - ${paymentAmountDisplay}`}
            </span>
            {!isCleared && (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors font-medium"
              >
                Update
              </button>
            )}
          </div>
        </div>

        {/* Mobile Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
            <div className="bg-white rounded-2xl p-4 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold text-gray-900">Update Payment</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">
                <span className="font-semibold">{enrollment?.userId?.name || enrollment?.userId?.teamName || enrollment?.userId?.schoolName || enrollment?.userName || 'Student'}</span>
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount (KES) *</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter amount paid"
                  min="1"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">Enter the total amount paid by the student</p>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleUpdatePayment}
                  disabled={loading || !paymentAmount}
                  className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    '✓ Mark as Cleared'
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmount('');
                  }}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop version
  return (
    <>
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          isCleared ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
        }`}>
          {isCleared ? '✓ Cleared' : '⏳ Pending'}
          {paymentAmountDisplay && ` - ${paymentAmountDisplay}`}
        </span>
        {!isCleared && (
          <button
            onClick={() => setShowPaymentModal(true)}
            className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors font-medium"
          >
            Update
          </button>
        )}
      </div>

      {/* Desktop Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Update Payment Status</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600">
                Student: <span className="font-semibold">{enrollment?.userId?.name || enrollment?.userId?.teamName || enrollment?.userId?.schoolName || enrollment?.userName || 'Unknown'}</span>
              </p>
              {(enrollment?.userId?.email || enrollment?.userEmail) && (
                <p className="text-sm text-gray-500">{enrollment.userId?.email || enrollment.userEmail}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount (KES) *</label>
              <input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter payment amount"
                min="1"
                step="0.01"
              />
              <p className="text-xs text-gray-500 mt-1">Enter the total amount paid by the student</p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleUpdatePayment}
                disabled={loading || !paymentAmount}
                className="flex-1 bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Updating...
                  </>
                ) : (
                  '✓ Mark as Cleared'
                )}
              </button>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentAmount('');
                }}
                disabled={loading}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
